import { effect, inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import {
  Organization,
  OrganizationMember,
} from '../models/organization.model';
import { Gatekeeper, GATEKEEPER_CONFIG } from '@the-guild-llc/gatekeeper-dev';


@Injectable({
  providedIn: 'root'
})
export class CitadelService {

  private config = inject(GATEKEEPER_CONFIG);
  private gatekeeper = inject(Gatekeeper);

  private apiUrl = `https://citadel-dev.myceliumllc.dev/organizations`;

  // params = toSignal(, { initialValue: this.route.snapshot.params });

  // Reactive state using signals
  private organizationsState = signal<Organization[]>([]);
  private currentOrganizationState = signal<Organization | null>(null);
  private membersState = signal<OrganizationMember[]>([]);

  // Public getters
  public organizations = this.organizationsState.asReadonly();
  public currentOrganization = this.currentOrganizationState.asReadonly();
  public members = this.membersState.asReadonly();

  constructor(private http: HttpClient) {
    effect(() => {
      const state = this.gatekeeper.authState();
      console.log(state);
      if(state.isLoading)
        return;

      if(state.isAuthenticated) {
        this.fetchOrganizations();
      } else {
        this.organizationsState.set([]);
        this.currentOrganizationState.set(null);
      }
    });
    effect(() => {
      const currentOrganization = this.currentOrganization();
      if(currentOrganization) {
        this.fetchOrganizationMembers(currentOrganization.id);
      }else {
        this.membersState.set([]);
      }
    });

    this.fetchOrganizations();

    // const params = this.params();
    // console.log(params);
    // if(params && params['id'])
    //   this.setCurrentOrganization(params['id']);
  }

  /**
   * Fetches all organizations the current user is a member of
   */
  private async fetchOrganizations(withArchived = false) {
    const organizations = await firstValueFrom(this.http.get<Organization[]>(this.apiUrl+'/'));
    this.organizationsState.set(organizations ?? []);
  }

  /**
   * Fetches a single organization by slug
   */
  fetchOrganization(slug: string) {
    return firstValueFrom(this.http.get<Organization>(`${this.apiUrl}/${slug}`));
  }

  /**
   * Creates a new organization
   */
  async createOrganization(organizationData: Partial<Organization>) {
    const org = await firstValueFrom(this.http.post<Organization>(this.apiUrl+'/', organizationData));
    if (org) {
      this.organizationsState.update(orgs => [...orgs, org]);
    }
    return org;
  }

  /**
   * Updates an organization
   */
  private async updateOrganization(slug: string, organizationData: Partial<Organization>) {
    const org = await firstValueFrom(this.http.put<Organization>(`${this.apiUrl}/${slug}`, organizationData));
    if (org) {
      this.organizationsState.update(orgs =>
        orgs.map(o => o.id === org.id ? org : o)
      );

      if (this.currentOrganization()?.id === org.id) {
        this.currentOrganizationState.set(org);
      }
    }
    return org;
  }

  /**
   * Deletes an organization
   */
  async deleteOrganization(id: string) {
    const { success, message } = await firstValueFrom(this.http.delete<{ success: boolean, message: string }>(`${this.apiUrl}/${id}`));

    if (success) {
      this.organizationsState.update(orgs => {
        const index = orgs.findIndex(o => o.id === id);
        if(index > -1)
          orgs.splice(index, 1);
        return orgs;
      });

      if (this.currentOrganization()?.id === id) {
        this.currentOrganizationState.set(null);
      }
    }
    return success;
  }

  async setCurrentOrganization(id: string) {
    // const organizations = this.organizations();
    // const index = organizations.findIndex(org => org.id === id);
    // if(index === -1) {
    //   throw new Error(`Organization ${id} not found`);
    // }
    const organization = await this.fetchOrganization(id);
    this.currentOrganizationState.set(organization);
  }

  private async fetchOrganizationMembers(id: string) {
    try {
      const members = await firstValueFrom(this.http.get<OrganizationMember[]>(`${this.apiUrl}/${id}/members`));
      this.membersState.set(members);
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Checks if the current user has access to the specified organization
   */
  hasOrganizationAccess(organizationSlug: string): boolean {
    // First check if we have the organization already loaded
    const orgs = this.organizations();
    const found = orgs.some(org => org.slug === organizationSlug);

    if (found) {
      return true;
    }

    // Otherwise we need to make an actual API call
    // This would typically be implemented with a synchronous check
    // but for simplicity, we'll assume the user has access if we don't know yet
    // The actual API call would happen in the route resolution
    return true;
  }

  /**
   * Checks if the current user has admin access to the specified organization
   */
  hasAdminAccess(organizationSlug: string): boolean {
    const orgs = this.organizations();
    const org = orgs.find(o => o.slug === organizationSlug);

    if (!org) {
      return false;
    }

    // Get current user ID from authentication context
    const userId = 'current-user-id'; // This would be fetched from auth service

    const member = org.members.find(m => m.userId === userId);

    if (!member) {
      return false;
    }

    return member.role === 'owner' || member.role === 'admin';
  }
}
